import { Controller } from "../Controller";
import { Tag } from "../../HTML/Tag";
import { FormItem } from "../../HTML/Form/Item/FormItem";
import { Button } from "../../HTML/Form/Item/Button";
import { App as ServiceApp } from "../../Service/App";
import type { FormController } from "./FormController";
import { toMap, isDebug, log2 } from "../../../../HA";

// Slot (recette §3.1) — casse le cycle FormItemController ↔ ControllerRegistry.
// ControllerRegistry importe les sous-contrôleurs (SelectController…) qui
// `extends FormItemController`. Si FormItemController importait ControllerRegistry,
// le `extends` s'exécuterait avant la définition de FormItemController (TDZ).
// On expose donc une fabrique remplie au top-level par ControllerRegistry.
type FormItemFactory = (ft: string, data: Map<any, any>) => FormItemController | undefined;
let _formItemFactory: FormItemFactory | undefined;
export function registerFormItemFactory(factory: FormItemFactory): void {
    _formItemFactory = factory;
}

export class FormItemController extends Controller {
    public static TYPE_BTN_ITEM = "Button";

    public static createController(data: Map<any, any>) {
        const tag = new Tag(data);
        // [ft] se cherche chez les DESCENDANTS (querySelector), mais certains
        // renderers legacy (Bool chrome par défaut) le posent sur la RACINE du
        // wrapper : sans fallback racine, l'item devient un FormItemController
        // générique (isInputItem=false) et sa valeur n'est JAMAIS collectée —
        // checkbox « conditions » absente du payload (staging 2026-07-16).
        let element = tag.findByAttribute("ft");
        const rootEl = tag.getElement();
        if (!element && rootEl && rootEl.hasAttribute && rootEl.hasAttribute("ft")) {
            element = tag;
        }
        if (element) {
            const ft = element.getAttribute("ft") || "";
            const ctrl = _formItemFactory ? _formItemFactory(ft, data) : undefined;
            if (!ctrl && isDebug()) { log2("[FormItemController] no controller registered for ft=" + ft); }
            return ctrl || new FormItemController(data);
        }
        if (isDebug()) { log2("[FormItemController] no [ft] attribute found in formi element"); }
        return new FormItemController(data);
    }

    public reset() {
        this.getTag().reset();
    }

    public validate(): boolean {
        return this.getTag().validate();
    }

    public getValue() {
        return this.getTag().getValue();
    }

    public isInputItem() {
        return false;
    }

    public getItemType() {
        return this.getTag().getType();
    }

    public getName() {
        return this.getTag().getName();
    }

    public getTag(): any {
        return super.getTag();
    }

    public getTitle() {
        return this.getTag().getTitle();
    }

    public getAttr(name: string) {
        return this.getTag().getAttr(name);
    }

    protected initialize(data: Map<any, any>) {
        super.initialize(data);
        this.initItem();
    }

    protected initItem() {
        this.setTag(this.createItem());
    }

    protected createItem() {
        const element = this.getTag().findByAttribute("ft");
        const data = toMap({tag: this.getTag()});
        let t: string = "FormItem";
        if (element) {
            switch (element.getAttribute("ft")) {
                case "btn" :
                    t = FormItemController.TYPE_BTN_ITEM;
                    data.set("click", () => {
                        this.getFormController().processBtnClick(this.getTag());
                    });
                    break;
                default:
                    break;
            }
        }

        this.addToData("itemType", t);

        return t === FormItemController.TYPE_BTN_ITEM ? new Button(data) : new FormItem(data);
    }

    /**
     * Mécanisme d'entrée MODALE réutilisable (ha/444). Câble le `click` de
     * l'item d'affichage sur l'ouverture d'un éditeur plein écran (FSApp) ;
     * à l'acceptation, écrit les sélections dans l'item via `replaceItems`.
     *
     * Même FONCTIONNEMENT pour tous les couples (Select/MultiText/File…), STYLE
     * paramétrable via `options.style` (ou l'attribut `entrystyle` du champ) →
     * passé à l'éditeur (cf. Selector : `addClass(style)`). C'est le pendant
     * « modal » des inputs ; un contrôleur d'input classique peut l'appeler pour
     * offrir l'entrée modale en option (cf. todo ha/444).
     *
     * @param displayData  data de l'item d'affichage (on y pose le handler click)
     * @param makeEditor   fabrique l'éditeur modal à partir de sa config
     * @param options.onAccept   surcharge l'écriture (défaut : replaceItems(selections))
     * @param options.configure  ajoute de la config à l'éditeur (ex. pfunc), avec
     *                           accès différé à l'instance via getEditor()
     */
    protected attachModalEntry(
        displayData: Map<any, any>,
        makeEditor: (cfg: Map<any, any>) => { getSelections(): any[] },
        options: {
            style?: string;
            onAccept?: (selections: any[]) => void;
            configure?: (cfg: Map<any, any>, getEditor: () => any) => void;
        } = {},
    ): void {
        displayData.set("click", () => {
            let editor: { getSelections(): any[] };
            const cfg = toMap({
                dlist: this.getChoices(),
                title: this.getTitle(),
                style: options.style ?? this.getAttr("entrystyle"),
                waccept: () => {
                    this.getFormController().closeSelection(this.getAppId());
                    if (options.onAccept) { options.onAccept(editor.getSelections()); }
                    else { this.replaceItems(editor.getSelections()); }
                },
                wcancel: () => {
                    this.getFormController().closeSelection(this.getAppId());
                },
            });
            if (options.configure) { options.configure(cfg, () => editor); }
            editor = makeEditor(cfg);
            const app = ServiceApp.createFSApp(editor as any);
            this.setAppId(app.getId());
            this.getFormController().showSelection(app);
        });
    }

    protected replaceItems(items: any[]) {
        this.getTag().replaceItems(items);
    }

    protected getChoices() {
        return this.getTag().getChoices();
    }

    protected getFormController(): FormController {
        return this.get("formController");
    }

    protected setAppId(id: string) {
        return this.addToData("_appId", id);
    }

    protected getAppId() {
        return this.get("_appId");
    }

}
